import React, {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useState,
} from "react";
import "aos/dist/aos.css";
import "animate.css";
import "../../global.scss";
import "antd/dist/antd.css";
import "bootstrap/dist/css/bootstrap.min.css";
import styled from "styled-components";
import Header from "./Header";
import MobileHeader from "./MobileHeader";
import Footer from "./Footer";
import Helmet from "react-helmet";
import "./css/PageLayout.scss";
import { navigate, Script } from "gatsby";
import { hotjar } from "react-hotjar";
import { v7 } from "uuid";
import { Crisp } from "crisp-sdk-web";
import { toast, ToastContainer } from "react-toastify";

interface PageLayoutProps {
  backgroundColor?: string;
  children: ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  opacity?: string;
  transition?: string;
  showPartnerNav?: boolean;
  containerPadding?: string;
  containerClassName?: string;
}

const PageLayout: React.FC<PropsWithChildren<PageLayoutProps>> = (
  props: PageLayoutProps
) => {
  const [containerPadding, setContainerPadding] = useState("148px");

  const search = typeof window !== "undefined" ? window.location.search : "";

  useEffect(() => {
    Crisp.configure(process.env.CRISP_WEBSITE_ID);
    Crisp.chat.close();
    Crisp.session.reset();
    let session = v7();
    const _session: string =
      sessionStorage.getItem("guest-user-crisp-chat") ?? "";
    if (_session === "") {
      sessionStorage.setItem("guest-user-crisp-chat", session);
    } else {
      session = _session;
    }
    Crisp.setTokenId(session);
    Crisp.user.setNickname("Guest User");

    // Disable Business Partner 4230
    let URL = new URLSearchParams(window.location.search);
    let bn = URL.get("bn");
    if (bn && bn == "4230") {
      navigate("/404");
    }
  }, [search]);

  useEffect(() => {
    const handleNavBarResize = () => {
      if (window.innerWidth > 1200) {
        setContainerPadding("148px");
      }
      if (window.innerWidth < 1200) {
        setContainerPadding("90px");
      }
      if (window.location.href.includes("coverage-map")) {
        setContainerPadding("50px");
        if (window.innerWidth < 767) {
          setContainerPadding("0px");
        }
      }
    };
    handleNavBarResize();
    window.addEventListener("resize", handleNavBarResize);

    return () => window.removeEventListener("resize", handleNavBarResize);
  }, [containerPadding]);

  useEffect(() => {
    const windowAlert = window.alert;
    window.alert = (message: string, timeOut: number = 5000) => {
      toast.info(message, {
        position: "top-right",
        autoClose: timeOut,
        theme: "light",
      });
    };
    // Store original alert function on window object for edge cases
    (window as any).originalAlert = windowAlert;
    return () => {
      window.alert = windowAlert;
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-TJKD8FQ"
          height="0"
          width="0"
          style={{ display: "none", visibility: "hidden" }}
        ></iframe>
      </noscript>
      <div
        style={{
          backgroundColor: props.backgroundColor,
          opacity: props.opacity,
          transition: props.transition,
        }}
        className="layout"
      >
        <LayoutWrapper>
          <Script src="https://maps.googleapis.com/maps/api/js?key=AIzaSyC_hzs4xVL5KROtCzSI3cfh7wpiYjBRvEc&libraries=places&callback=googleMapsLoaded"></Script>
          <Script src="https://contentkit.t-mobile.com/prd/web-content-kit.js"></Script>
          {/* PROD Token: pk.eyJ1IjoidG11cy1kaWdpdGFsIiwiYSI6ImNsOHl0dDBzeTAwMGMzcW4zbDdpazNkangifQ.yi1BRDWqB5eRtbrQwFQCxw */}
          <Script>{`
            window.tmobkit = {
              cdnURL: 'https://contentkit.t-mobile.com/prd',
              apiURL: 'https://www.t-mobile.com',
              map: {
                access_token: '${process.env.GATSBY_TMOBKIT_TOKEN}',
                preloadMapgl: false
              }
            };
          `}</Script>
          {/* To satisfy the Google Maps required callback parameter we're just loading it into a function and doing nothing because we don't need it until the GoogleAutoCompleteAddress component needs it. */}
          <Script>{`function googleMapsLoaded(){ console.log('Google Maps Loaded'); window.isGoogleMapsLoaded = true; }`}</Script>
          {/* this is where the script tag for GTM used to be 'gtm.start' */}
          {/* <Script></Script> */}
          <Helmet>
            {/* <head> tag elements go here */}
            <meta name="etag" content="1.0.0" />
            <meta name="referrer" content="origin" />
            <meta name="cache-control" content="max-age=300" />
            <meta
              name="description"
              content="PhoneBox offers unlimited nation-wide calls and texts, 1,000 minutes of international calls. Multilingual customer service, free shipping & more!"
            />
            <meta
              property="og:description"
              content="PhoneBox offers unlimited nation-wide calls and texts, 1,000 minutes of international calls. Multilingual customer service, free shipping & more!"
            />
          </Helmet>

          {!props.hideHeader && (
            <HeaderWrapper>
              <ShouldHideHeader>
                <Header />
              </ShouldHideHeader>
              <ShouldHideMobileHeader>
                <MobileHeader />
              </ShouldHideMobileHeader>
            </HeaderWrapper>
          )}

          <ContentWrapper
            containerPadding={containerPadding}
            className={props.containerClassName}
          >
            {props.children}
          </ContentWrapper>
          {!props.hideFooter && <Footer />}
        </LayoutWrapper>
      </div>
    </>
  );
};

const isBrowser = typeof window !== "undefined";

if (isBrowser) {
  hotjar.initialize(1956762, 6);
}

const ShouldHideHeader = styled.div`
  @media (max-width: 1200px) {
    display: none;
  }
`;

const ShouldHideMobileHeader = styled.div`
  @media (min-width: 1200px) {
    display: none;
  }
`;

const LayoutWrapper = styled.div`
  position: relative;
  min-height: 100vh;
`;

const HeaderWrapper = styled.div`
  position: fixed !important;
  top: 0px;
  width: 100vw;
  z-index: 3;
`;

const ContentWrapper = styled.div<{ containerPadding: string }>`
  padding: ${(props) =>
      props.containerPadding ? props.containerPadding : "100px"}
    0px;
`;

export default PageLayout;
